/**
 * 举报与处置（D66–D68）。
 *
 * 处置的影响面大（帖子从所有视图消失、账号整个被隐藏），成功后把时间线、
 * 帖子、搜索、通知一律作废重取，而不是在缓存里逐条摘 —— 管理动作不频繁，
 * 正确比省一次请求重要。
 */

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { adminApi, reportsApi } from '@/api/endpoints'
import type { CreateReportInput } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { ReportAction, ReportStatus } from '@/api/types'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'
import { removePostEverywhere } from '@/lib/cache'

export function useReport() {
  return useMutation({ mutationFn: (input: CreateReportInput) => reportsApi.create(input) })
}

export function useAdminReports(status: ReportStatus, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.adminReports(status),
    queryFn: ({ pageParam }) => adminApi.reports(status, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  })
}

export function useAdminActions(enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.adminActions(),
    queryFn: ({ pageParam }) => adminApi.actions(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  })
}

/** 处置之后，站内几乎所有列表都可能变了 */
function useInvalidateAfterModeration() {
  const client = useQueryClient()
  return () => {
    client.invalidateQueries({ queryKey: keys.adminRoot() })
    client.invalidateQueries({ queryKey: keys.timelineRoot() })
    client.invalidateQueries({ queryKey: ['post'] })
    client.invalidateQueries({ queryKey: ['user'] })
    client.invalidateQueries({ queryKey: keys.searchRoot() })
    client.invalidateQueries({ queryKey: keys.notificationsRoot() })
    client.invalidateQueries({ queryKey: keys.suggestions() })
  }
}

export function useResolveReport() {
  const t = useT()
  const toast = useToast()
  const invalidate = useInvalidateAfterModeration()
  return useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: ReportAction; note?: string }) =>
      adminApi.resolve(id, action, note),
    onSuccess: invalidate,
    onError: () => toast.error(t('toast.actionFailed')),
  })
}

export function useAdminRemovePost() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()
  const invalidate = useInvalidateAfterModeration()
  return useMutation({
    mutationFn: ({ postId, note }: { postId: string; note?: string }) =>
      adminApi.removePost(postId, note),
    onSuccess: (_result, { postId }) => {
      removePostEverywhere(client, postId)
      invalidate()
      toast.show(t('toast.postRemoved'))
    },
    onError: () => toast.error(t('toast.actionFailed')),
  })
}

export function useSuspendToggle() {
  const t = useT()
  const toast = useToast()
  const invalidate = useInvalidateAfterModeration()
  return useMutation({
    mutationFn: ({
      username,
      suspended,
      note,
    }: {
      username: string
      suspended: boolean
      note?: string
    }) => (suspended ? adminApi.unsuspend(username, note) : adminApi.suspend(username, note)),
    onSuccess: (user, { suspended }) => {
      invalidate()
      toast.show(
        suspended
          ? t('toast.userUnsuspended', { username: user.username })
          : t('toast.userSuspended', { username: user.username }),
      )
    },
    onError: () => toast.error(t('toast.actionFailed')),
  })
}
